<template>
    <div
        class="c-header-main__cart js-header-main-cart"
        :class="{ 'is-part-of-layout': hasMinicartLayout }"
        ref="header-main-cart"
    >
        <a
            href="/nakupny-kosik"
            class="c-minicart-trigger"
            :class="{
                'is-active': isOpen,
                'is-part-of-layout': hasMinicartLayout,
            }"
            :data-quantity="count"
            @click="handleTriggerClick"
        >
            <i class="ico ico-cart" aria-hidden="true"></i>
            <span class="c-text" v-if="isOpen">{{ $t("Minicart.Trigger.title") }}</span>
            <span class="c-text" v-else>{{ $t("Minicart.Trigger.titleCollapsed") }}</span>
            <span class="c-badge c-badge--secondary" v-if="isInitialized && !isEmpty">{{
                count
            }}</span>
            <span
                class="c-price"
                :class="{ 'is-loading': !isInitialized || isLoading }"
                v-html="formatPrice(pricesWithVat ? totalPrice : subtotalPrice)"
            ></span>
            <i class="ico ico-chevron-down" aria-hidden="true"></i>
        </a>

        <div
            v-if="isOpen"
            class="c-minicart"
            :class="{
                'is-loading': isLoading,
                'is-placeholder': !isInitialized,
            }"
        >
            <div v-if="isInitialized && !isEmpty && competition" class="c-minicart__competition">
                <div
                    class="c-competition-box c-competition-box--minicart"
                    :class="{ 'is-completed': competitionIsCompletedWithCart }"
                >
                    <div class="c-competition-box__main">
                        <div class="c-competition-box__main__icon" aria-hidden="true">
                            <img class="c-img" src="/img/icons/icon-trophy.svg" alt="" />
                        </div>

                        <div class="c-competition-box__main__content">
                            <div class="sc-title">
                                {{ competition.name || $t("Minicart.competition.title") }}
                            </div>

                            <div class="sc-amount">
                                <strong v-html="formatPrice(competitionSpentWithCart)"></strong>
                                <span class="sc-target">
                                    / <span v-html="formatPrice(competitionTargetWithCart)"></span
                                ></span>
                            </div>
                        </div>
                    </div>

                    <div class="c-competition-box__tracker">
                        <div
                            class="sc-tracker"
                            :style="{ width: competitionPercentWithCart + '%' }"
                        ></div>
                    </div>

                    <div class="c-competition-box__footer">
                        <div class="sc-note" v-if="competitionIsCompletedWithCart">
                            {{ $t("Minicart.competition.completedAfterPurchase") }}
                        </div>

                        <div class="sc-note" v-else v-html="competitionRemainingTextWithCart"></div>

                        <button
                            type="button"
                            class="c-link c-link--underlined"
                            data-toggle="modal"
                            data-target="#competition-modal"
                        >
                            {{ $t("Minicart.competition.link") }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="c-minicart__placeholder" v-if="!isInitialized"></div>

            <div class="c-minicart__empty" v-else-if="isEmpty">
                <i class="ico ico-cart"></i>
                <div class="c-title">{{ $t("Minicart.emptyText") }}</div>
                <div class="c-text">{{ $t("Minicart.emptyText2") }}</div>
            </div>

            <template v-else>
                <div class="c-minicart__products-wrapper">
                    <div class="c-minicart__products">
                        <div
                            class="c-minicart-type"
                            v-for="(mainCategory, mainCategoryKey) in productCategories"
                            :key="`main-category-${mainCategoryKey}`"
                        >
                            <button
                                class="c-minicart-type__trigger c-btn"
                                :class="{ 'is-open': isMainCategoryOpen(mainCategoryKey) }"
                                v-if="Object.keys(productCategories).length > 1"
                                @click="toggleMainCategory(mainCategoryKey)"
                            >
                                <i class="ico ico-chevron-down-thick"></i>
                                <span class="c-name"> {{ mainCategory.name }}</span>
                                <span class="c-badge c-badge--light-grey">{{
                                    mainCategory.items.length
                                }}</span>
                                <span
                                    class="c-price"
                                    v-html="
                                        formatPrice(
                                            pricesWithVat
                                                ? mainCategory.price_vat
                                                : mainCategory.price
                                        )
                                    "
                                ></span>
                            </button>

                            <div
                                class="c-minicart-type__products"
                                v-show="isMainCategoryOpen(mainCategoryKey)"
                            >
                                <MinicartItem
                                    v-for="item in mainCategory.items"
                                    :key="item.cart_id"
                                    :item="item"
                                    :gifts="getGiftsForProduct(item.id)"
                                    :isUpdatingQuantity="updatingQuantity[item.cart_id]"
                                    @remove="removeItem"
                                    @removeGift="removeGift"
                                    @returnGift="returnGift"
                                    @spinningQuantity="spinningQuantity"
                                    @updateQuantity="updateQuantity"
                                />
                            </div>
                        </div>

                        <div
                            v-if="standaloneGifts.length"
                            class="c-minicart-type__products c-minicart-type__products--standalone-gifts"
                        >
                            <a
                                v-for="gift in standaloneGifts"
                                :key="'standalone-gift-' + gift.cart_id"
                                :href="gift.full_link"
                                class="c-gift-product-thumb-wrapper"
                            >
                                <div
                                    class="c-gift-product-thumb"
                                    :class="{ 'is-refused': gift.is_refused }"
                                >
                                    <button
                                        v-if="!gift.is_refused"
                                        type="button"
                                        class="c-gift-product-thumb__delete js-remove-gift"
                                        :title="$t('MinicartItem.removeGift')"
                                        @click.prevent.stop="removeGift(gift.cart_id)"
                                    >
                                        <i class="ico ico-close"></i>
                                    </button>
                                    <button
                                        v-if="gift.is_refused"
                                        type="button"
                                        class="c-gift-product-thumb__delete js-return-gift"
                                        :title="$t('MinicartItem.returnGift')"
                                        @click.prevent.stop="returnGift(gift.cart_id)"
                                    >
                                        <i class="ico ico-repeat"></i>
                                    </button>
                                    <div class="c-gift-product-thumb__image">
                                        <div class="sc-icon">
                                            <i class="ico ico-gift"></i>
                                        </div>
                                        <img
                                            class="c-img"
                                            :src="thumbnail(gift.image, 'xs-cutout')"
                                            :alt="gift.name"
                                        />
                                    </div>
                                    <div class="c-gift-product-thumb__title is-desktop">
                                        {{ $t("Minicart.giftProductTitleDesktop") }}
                                    </div>
                                    <div class="c-gift-product-thumb__title is-mobile">
                                        {{ $t("Minicart.giftProductTitleMobile") }}
                                    </div>
                                    <div class="c-gift-product-thumb__name">
                                        <span
                                            v-if="gift.quantity > 1"
                                            class="c-gift-product-thumb__qty"
                                            >{{ gift.quantity }}x</span
                                        >
                                        {{ gift.name }}
                                    </div>
                                    <div class="c-gift-product-thumb__price">
                                        <span>{{ $t("Minicart.giftProductPriceLabel") }}</span>
                                        {{ formatPrice(gift.promotion.original_price) }}
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </template>

            <div class="c-minicart__subtotal">
                <a
                    class="c-btn c-btn--cart"
                    :class="{ 'is-empty': isEmpty }"
                    href="/nakupny-kosik"
                    :title="$t('Minicart.proceedToCheckout')"
                >
                    <span class="c-text">
                        {{ $t("Minicart.proceedToCheckout") }}
                        <i aria-hidden="true" class="ico ico-chevron-right"></i>
                    </span>

                    <span
                        class="c-price"
                        :class="{ 'is-loading': !isInitialized || isLoading }"
                        v-html="formatPrice(pricesWithVat ? totalPrice : subtotalPrice)"
                    ></span>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { showHeaderOverlay, hideHeaderOverlay } from "Functions/overlay";
import RshopMinicart from "FrontendComponents/Minicart";
import { formatPrice } from "FrontendJsAssets/common/format";
import client from "FrontendJsAssets/common/client";
import { CancelToken, isCancel } from "axios";
import { checkResolution } from "Functions/helpers";
import { calculateHeaderVisibleHeight } from "Functions/window";
import MinicartItem from "VueComponents/MinicartItem";
import { getCookie, setCookie } from "Functions/cookie";
import { thumbnail } from "Functions/thumbnail";

const lang = document.querySelector("html").getAttribute("lang");
let locale;

import(`Locale/${lang}`).then((module) => {
    locale = module.Swal;
});

export default {
    extends: RshopMinicart,
    components: {
        MinicartItem: MinicartItem,
    },
    created() {
        this.fetchCart();

        if (typeof rEvent.on === "function") {
            rEvent.on("Cart.product.after_add", (response) => {
                this.fetchCart();
            });

            rEvent.on("Cart.addedProduct", (response) => {
                this.fetchCart();
            });

            rEvent.on("Cart.addedWishlist", (response) => {
                this.fetchCart();
            });

            rEvent.on("Cart.customFetchCart", () => {
                this.fetchCart();
            });

            rEvent.on("Cart.removedProduct", () => {
                this.fetchCart();
            });

            rEvent.on("Cart.customCancelFetchCart", () => {
                this.cancelFetchCart();
            });
        }
    },
    data() {
        return {
            isOpen: false,
            pricesWithVat: window.Eshop.options.pricesWithVat,
            hasMinicartLayout: window.Eshop.custom.hasMinicartLayout,
            competition: (window.Eshop && window.Eshop.competition) || null,
            freeShipping: [],
            items: [],
            collapsedMainCategories: {},
            totals: [],
            fetchCancelToken: null,
            isInitialized: false,
            fetchingProducts: true,
            fetchingTotals: true,
            updatingQuantity: {},
            updatingQuantityTimeouts: {},
            updateQuantityCancelTokens: {},
            scrollTicking: false,
            productsGifts: [],
            standaloneGiftIds: [],
        };
    },
    mounted() {
        if (this.hasMinicartLayout && checkResolution("md;lg")) {
            this.isOpen = !getCookie("minicart_is_open") || getCookie("minicart_is_open") === "1";
        }

        if (window.localStorage.getItem("minicart_collapsed_main_categories")) {
            this.collapsedMainCategories = JSON.parse(
                window.localStorage.getItem("minicart_collapsed_main_categories")
            );
        }

        if (checkResolution("md;lg")) {
            document.addEventListener("scroll", (e) => {
                if (this.isOpen && !this.scrollTicking) {
                    setTimeout(() => {
                        this.recalculateMinicartOffset();
                        this.scrollTicking = false;
                    }, 200);

                    this.scrollTicking = true;
                }
            });
        }

        window.rEvent.on("Window.resized", () => {
            if (this.isOpen && !checkResolution("md;lg")) {
                this.toggleMinicart(false);
            }
        });
    },
    computed: {
        count() {
            return this.totals.count || 0;
        },
        itemsCount() {
            if (this.items) {
                return this.items.reduce((count, item) => {
                    return count + Math.round(parseFloat(item.quantity) / item.step) * item.step;
                }, 0);
            }

            return 0;
        },
        isEmpty() {
            return this.itemsCount === 0;
        },
        totalPrice() {
            return this.totals.total;
        },
        subtotalPrice() {
            return this.totals.subtotal;
        },
        savings() {
            return Math.abs(this.totals.discount);
        },
        hasFreeShipping() {
            return this.freeShipping ? this.freeShipping.free_shipping_amount <= 0 : false;
        },
        freeShippingText() {
            if (!this.freeShipping) {
                return null;
            }

            return this.freeShipping[
                this.hasFreeShipping ? "free_shipping_text" : "not_free_shipping_text"
            ];
        },
        isLoading() {
            return (
                this.fetchingProducts ||
                this.fetchingTotals ||
                Object.keys(this.updatingQuantity).length > 0
            );
        },
        competitionRemainingText() {
            if (!this.competition || this.competition.isCompleted) {
                return "";
            }

            return this.$t("Minicart.competition.remaining", {
                amount: "<strong>" + formatPrice(this.competition.remainingToNext) + "</strong>",
                milestone: this.competition.nextMilestone
                    ? this.competition.nextMilestone.order
                    : "",
            });
        },
        // Competition preview values including current cart total
        competitionSpentWithCart() {
            if (!this.competition) {
                return 0;
            }
            const cartTotal = this.pricesWithVat ? this.totalPrice : this.subtotalPrice;
            const spent = (this.competition.baseSpentAmount || this.competition.spentAmount) + (cartTotal || 0);
            return Math.min(spent, this.competition.finalThreshold);
        },
        competitionTargetWithCart() {
            if (!this.competition) {
                return 0;
            }
            // recalculate target based on spent with cart
            const spent = this.competitionSpentWithCart;
            const milestones = this.competition.milestones || [];
            for (const milestone of milestones) {
                if (spent + 0.00001 < milestone.threshold) {
                    return milestone.threshold;
                }
            }
            return this.competition.finalThreshold;
        },
        competitionPercentWithCart() {
            if (!this.competition) {
                return 0;
            }
            const spent = this.competitionSpentWithCart;
            const target = this.competitionTargetWithCart;
            return target > 0 ? Math.min(100, (spent / target) * 100).toFixed(1) : 100;
        },
        competitionIsCompletedWithCart() {
            if (!this.competition) {
                return false;
            }
            const spent = this.competitionSpentWithCart;
            return spent + 0.00001 >= this.competition.finalThreshold;
        },
        competitionRemainingWithCart() {
            if (!this.competition) {
                return 0;
            }
            return Math.max(0, this.competitionTargetWithCart - this.competitionSpentWithCart);
        },
        competitionNextMilestoneWithCart() {
            if (!this.competition) {
                return null;
            }
            const spent = this.competitionSpentWithCart;
            const milestones = this.competition.milestones || [];
            for (const milestone of milestones) {
                if (spent + 0.00001 < milestone.threshold) {
                    return milestone;
                }
            }
            return null;
        },
        competitionRemainingTextWithCart() {
            if (!this.competition || this.competitionIsCompletedWithCart) {
                return "";
            }

            const nextMilestone = this.competitionNextMilestoneWithCart;
            return this.$t("Minicart.competition.remainingAfterPurchase", {
                amount: "<strong>" + formatPrice(this.competitionRemainingWithCart) + "</strong>",
                milestone: nextMilestone ? nextMilestone.order : "",
            });
        },
        productCategories() {
            let groups = {
                _0: {
                    name: this.$t("Minicart.otherMainCategory"),
                    items: [],
                    price: 0,
                    price_vat: 0,
                },
            };

            for (const item of this.items) {
                let mainCategoryId = `_${item.custom_main_category?.id || 0}`;

                // do 10 produktov nezgroupovat
                if (this.items.length <= 10) {
                    mainCategoryId = "_0";
                }

                if (!groups[mainCategoryId]) {
                    const newGroup = {};
                    newGroup[mainCategoryId] = {
                        name: item.custom_main_category.name,
                        items: [],
                        price: 0,
                        price_vat: 0,
                    };

                    groups = { ...newGroup, ...groups };
                }

                groups[mainCategoryId].items.push(item);
                groups[mainCategoryId].price += item.price;
                groups[mainCategoryId].price_vat += item.price_vat;
            }

            if (!groups["_0"].items.length) {
                delete groups["_0"];
            }

            return groups;
        },
        standaloneGifts() {
            if (!this.standaloneGiftIds.length) {
                return [];
            }

            const standaloneSet = new Set(this.standaloneGiftIds.map(String));

            return this.items.filter((item) => standaloneSet.has(String(item.id)));
        },
    },
    methods: {
        thumbnail,
        cancelFetchCart() {
            if (this.fetchCancelToken) {
                this.fetchCancelToken.cancel();
            }

            this.fetchCancelToken = CancelToken.source();
        },
        buildProductToGiftsSet(promotionItemIds, onceGiftIds = []) {
            const productToGifts = {}; // productId -> Set<giftId>
            const onceSet = new Set(onceGiftIds.map(String));

            for (const [giftIdStr, productIds] of Object.entries(promotionItemIds)) {
                const giftId = Number(giftIdStr);

                if (onceSet.has(giftIdStr)) {
                    // Standalone gift: don't assign to any product, will be rendered separately
                    continue;
                } else {
                    // Regular BuyNotSaleable: assign to every triggering product
                    for (const productId of productIds) {
                        if (!productToGifts[productId]) {
                            productToGifts[productId] = new Set();
                        }
                        productToGifts[productId].add(giftId);
                    }
                }
            }

            return productToGifts;
        },
        getGiftsForProduct(productId) {
            const giftIds = this.productsGifts[productId];

            if (!giftIds) {
                return [];
            }

            const giftProducts = this.items.filter((product) => giftIds.has(product.id));

            return giftProducts;
        },
        fetchCart() {
            this.cancelFetchCart();
            this.fetchProducts();
            this.fetchTotals();
        },
        fetchProducts() {
            this.fetchingProducts = true;

            client
                .get("/cart/fetch-products", {
                    cancelToken: this.fetchCancelToken.token,
                })
                .then((response) => {
                    if (!response.data.status) {
                        return;
                    }

                    this.items = Object.values(response.data.data.items);
                    this.standaloneGiftIds = response.data.data.onceGiftIds || [];

                    this.productsGifts = this.buildProductToGiftsSet(
                        response.data.data.promotionItemIds,
                        this.standaloneGiftIds
                    );

                    rEvent.dispatch("Cart.fetchedProducts", {
                        response: response.data,
                    });

                    this.fetchingProducts = false;
                })
                .catch((error) => {
                    if (!isCancel(error)) {
                        this.fetchingProducts = false;
                    }
                });
        },
        fetchTotals() {
            this.fetchingTotals = true;

            client
                .get("/cart/fetch-totals", {
                    cancelToken: this.fetchCancelToken.token,
                })
                .then((response) => {
                    if (!response.data.status) {
                        return;
                    }

                    this.freeShipping = response.data.data.free_shipping;
                    this.totals = response.data.data.totals;

                    rEvent.dispatch("Custom.Cart.fetchedTotals", {
                        response: response.data,
                    });

                    this.fetchingTotals = false;
                })
                .catch((error) => {
                    if (!isCancel(error)) {
                        this.fetchingTotals = false;
                    }
                });
        },
        formatPrice(price) {
            return formatPrice(price);
        },
        removeItem(cartId) {
            let itemIndex = -1;

            // find item
            const item = this.items.find((item, index) => {
                if (item.cart_id === cartId) {
                    itemIndex = index;
                    return true;
                }

                return false;
            });

            if (itemIndex === -1) {
                return false;
            }

            // remove item
            window.Eshop.confirm(
                locale.DeleteProduct.title,
                {
                    denyButtonText: locale.DeleteProduct.deny,
                    confirmButtonText: locale.DeleteProduct.confirm,
                    customClass: {
                        confirmButton: "c-btn c-btn--delete",
                        denyButton: "c-btn c-btn--light-grey",
                    },
                },
                () => {
                    document.querySelector(".swal2-confirm").classList.add("is-loading");

                    return client.post(`/remove-item/${cartId}`).then((response) => {
                        window.rEvent.dispatch("Cart.product.removed", response.data);
                        window.rEvent.dispatch("Cart.removedProduct", {
                            response: response.data,
                            product: item,
                        });
                    });
                },
                (result) => {
                    if (result.isConfirmed) {
                        this.items.splice(itemIndex, 1);
                        this.fetchCart();
                    }
                }
            );
        },
        removeGift(cartId) {
            let itemIndex = -1;

            // find item
            const item = this.items.find((item, index) => {
                if (item.cart_id === cartId) {
                    itemIndex = index;
                    return true;
                }

                return false;
            });

            if (itemIndex === -1) {
                return false;
            }

            // remove gift
            window.Eshop.confirm(
                locale.DeleteGift.title,
                {
                    denyButtonText: locale.DeleteGift.deny,
                    confirmButtonText: locale.DeleteGift.confirm,
                    customClass: {
                        confirmButton: "c-btn c-btn--delete",
                        denyButton: "c-btn c-btn--light-grey",
                    },
                },
                () => {
                    document.querySelector(".swal2-confirm").classList.add("is-loading");

                    return client.post(`/remove-gift/${cartId}`).then((response) => {
                        window.rEvent.dispatch("Cart.gift.removed", response.data);
                        window.rEvent.dispatch("Cart.removedGift", {
                            response: response.data,
                            product: item,
                        });
                    });
                },
                (result) => {
                    if (result.isConfirmed) {
                        this.fetchCart();
                    }
                }
            );
        },
        returnGift(cartId) {
            let itemIndex = -1;

            // find item
            const item = this.items.find((item, index) => {
                if (item.cart_id === cartId) {
                    itemIndex = index;
                    return true;
                }

                return false;
            });

            if (itemIndex === -1) {
                return false;
            }

            // return gift
            window.Eshop.confirm(
                locale.ReturnGift.title,
                {
                    denyButtonText: locale.ReturnGift.deny,
                    confirmButtonText: locale.ReturnGift.confirm,
                    customClass: {
                        confirmButton: "c-btn c-btn--primary",
                        denyButton: "c-btn c-btn--light-grey",
                    },
                },
                () => {
                    document.querySelector(".swal2-confirm").classList.add("is-loading");

                    return client.post(`/return-gift/${cartId}`).then((response) => {
                        window.rEvent.dispatch("Cart.gift.returned", response.data);
                        window.rEvent.dispatch("Cart.returnedGift", {
                            response: response.data,
                            product: item,
                        });
                    });
                },
                (result) => {
                    if (result.isConfirmed) {
                        this.fetchCart();
                    }
                }
            );
        },
        spinningQuantity(cartId) {
            if (!this.updatingQuantity[cartId]) {
                return;
            }

            this.$set(this.updatingQuantity, cartId, true);
            this.cancelUpdateQuantity(cartId);
            this.cancelFetchCart();

            if (this.updatingQuantityTimeouts[cartId]) {
                clearTimeout(this.updatingQuantityTimeouts[cartId]);
            }
        },
        cancelUpdateQuantity(cartId) {
            const quantityCancelToken = this.updateQuantityCancelTokens[cartId];

            if (quantityCancelToken) {
                quantityCancelToken.cancel();
            }

            this.updateQuantityCancelTokens[cartId] = CancelToken.source();
        },
        updateQuantity(cartId, quantity) {
            const isFirstUpdate = !this.updatingQuantity[cartId];

            this.$set(this.updatingQuantity, cartId, true);
            this.cancelUpdateQuantity(cartId);
            this.cancelFetchCart();

            if (this.updatingQuantityTimeouts[cartId]) {
                clearTimeout(this.updatingQuantityTimeouts[cartId]);
            }

            this.$set(
                this.updatingQuantityTimeouts,
                cartId,
                setTimeout(
                    () => {
                        this.postNewQuantity(cartId, quantity);

                        this.$delete(this.updatingQuantityTimeouts, cartId);
                    },
                    isFirstUpdate ? 0 : 650
                )
            );
        },
        postNewQuantity(cartId, quantity) {
            client
                .post(
                    `/change-item-quantity/${cartId}`,
                    {
                        quantity: quantity,
                    },
                    {
                        cancelToken: this.updateQuantityCancelTokens[cartId].token,
                    }
                )
                .then((response) => {
                    const data = response.data;

                    if (!data.status) {
                        return;
                    }

                    this.items.forEach((item, index) => {
                        if (item.cart_id == cartId) {
                            this.$set(
                                this.items,
                                index,
                                Object.assign(this.items[index], {
                                    quantity: data.item.quantity,
                                    price: data.item.price,
                                    price_vat: data.item.price_vat,
                                })
                            );
                        }
                    });

                    this.totals.total = data.totals.total;
                    this.totals.subtotal = data.totals.subtotal;
                    this.afterQuantityFetch(cartId);

                    rEvent.dispatch("Custom.Cart.updatedItem", { response: data });
                    // GA4/GTM tracking počúva na Cart.product.updated — zmena počtu
                    // v minicarte sa inak netrackovala (LNS-1706)
                    rEvent.dispatch("Cart.product.updated", data.item);
                })
                .catch((error) => {
                    if (!client.isCancel(error)) {
                        this.afterQuantityFetch(cartId);
                    }
                });
        },
        afterQuantityFetch(cartId) {
            if (!this.updatingQuantityTimeouts[cartId]) {
                this.$delete(this.updatingQuantity, cartId);
            }

            if (!Object.keys(this.updatingQuantity).length) {
                this.fetchCart();
            }
        },
        handleClickOutside($event) {
            if (
                this.isOpen &&
                !this.$el.contains($event.target) &&
                !$event.target.closest(".swal2-container") &&
                !$event.target.closest(".notify")
            ) {
                this.toggleMinicart(false);
            }
        },
        handleTriggerClick(event) {
            event.preventDefault();
            this.toggleMinicart();
        },
        toggleMinicart(value) {
            if (typeof value !== "boolean") {
                value = !this.isOpen;
            }

            this.isOpen = value;

            if (!checkResolution("md;lg") && value) {
                document.addEventListener("click", this.handleClickOutside);

                window.scroll({
                    top: 0,
                    left: 0,
                    behavior: "instant",
                });

                // výška minikošíka sa ráta od spodku hlavičky po odscrollovaní hore,
                // vrátane android smart app bannera nad ňou (LNS-1837)
                calculateHeaderVisibleHeight();
            } else {
                document.removeEventListener("click", this.handleClickOutside);
            }
        },
        recalculateMinicartOffset() {
            const cartBounds = this.$refs["header-main-cart"].getBoundingClientRect();

            document.documentElement.style.setProperty(
                "--minicart-offset",
                `${Math.round(cartBounds.top)}px`
            );
        },
        toggleMainCategory(mainCategoryKey) {
            if (this.collapsedMainCategories[mainCategoryKey]) {
                this.$delete(this.collapsedMainCategories, mainCategoryKey);
            } else {
                this.$set(this.collapsedMainCategories, mainCategoryKey, true);
            }
        },
        isMainCategoryOpen(mainCategoryKey) {
            return (
                Object.keys(this.productCategories).length <= 1 ||
                !this.collapsedMainCategories[mainCategoryKey]
            );
        },
    },
    watch: {
        isLoading(value) {
            if (!value && !this.isInitialized) {
                this.isInitialized = true;
            }
        },
        isOpen(value) {
            if (value) {
                this.recalculateMinicartOffset();

                if (!checkResolution("md;lg")) {
                    showHeaderOverlay("minicart");
                }
            } else {
                hideHeaderOverlay("minicart");
            }

            if (checkResolution("md;lg") && this.hasMinicartLayout) {
                setCookie("minicart_is_open", value ? 1 : 0, 1);
            }
        },
        collapsedMainCategories(value) {
            window.localStorage.setItem(
                "minicart_collapsed_main_categories",
                JSON.stringify(value)
            );
        },
    },
};
</script>
